// mindmetal-app/web/src/pages/employee/mindmetal/TestUsersTab.tsx
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Plus, User, Beaker, Eye, Trash2, X } from 'lucide-react'
import { Card, CardContent, Button, Input, Textarea, Badge } from '@personic/shared'
import {
  useTestUsers,
  useCreateTestUser,
  useDeleteTestUser,
} from '@/hooks/useMindMetaLTestUsers'
import { CoverageIndicator } from '@/components/mindmetal/CoverageIndicator'
import { ExportMenu } from '@/components/mindmetal/ExportMenu'
import { DistillationPanel } from '@/components/mindmetal/DistillationPanel'

export function TestUsersTab() {
  const [page, setPage] = useState(1)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDistillModal, setShowDistillModal] = useState<string | null>(null)
  const [newUser, setNewUser] = useState({ display_name: '', description: '' })

  const { data, isLoading } = useTestUsers(page, 20)
  const createUser = useCreateTestUser()
  const deleteUser = useDeleteTestUser()

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    await createUser.mutateAsync(newUser)
    setNewUser({ display_name: '', description: '' })
    setShowCreateModal(false)
  }

  const handleDelete = async (canonicalId: string) => {
    if (confirm(`Delete test user ${canonicalId}?`)) {
      await deleteUser.mutateAsync(canonicalId)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-midnight-800">Test Users</h3>
          <p className="text-sm text-warm-500">{data?.total || 0} test users total</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} leftIcon={<Plus className="w-4 h-4" />}>
          Create Test User
        </Button>
      </div>

      {/* Test Users List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-copper-600" />
        </div>
      ) : (
        <div className="space-y-3">
          {data?.test_users.map((user, i) => (
            <motion.div
              key={user.canonical_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Card variant="outlined" padding="none" className="hover:shadow-soft transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-10 h-10 rounded-full bg-copper-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-copper-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-copper-600">
                            {user.canonical_id}
                          </span>
                          <span className="font-medium text-midnight-800">{user.display_name}</span>
                        </div>
                        {user.description && (
                          <p className="text-sm text-warm-500 mt-1 line-clamp-1">
                            {user.description}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-2">
                          <CoverageIndicator
                            level={user.coverage_level}
                            observationCount={user.total_observation_count}
                            showProgress={false}
                          />
                          <span className="text-xs text-warm-500">
                            {user.distillation_count} distillations
                          </span>
                          {user.stability_indicator && (
                            <Badge
                              variant={
                                user.stability_indicator === 'stable'
                                  ? 'sage'
                                  : user.stability_indicator === 'stabilizing'
                                    ? 'warning'
                                    : 'default'
                              }
                              size="sm"
                            >
                              {user.stability_indicator}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Link to={`/employee/mindmetal/profiles/${user.canonical_id}`}>
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowDistillModal(user.canonical_id)}
                      >
                        <Beaker className="w-4 h-4" />
                      </Button>
                      <ExportMenu canonicalId={user.canonical_id} />
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(user.canonical_id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}

          {data?.test_users.length === 0 && (
            <div className="text-center py-12 text-warm-500">
              No test users yet. Create one to get started.
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {data && data.total_pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-warm-500">
            Page {page} of {data.total_pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
            disabled={page === data.total_pages}
          >
            Next
          </Button>
        </div>
      )}

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-warm-200">
                <h3 className="font-semibold text-midnight-800">Create Test User</h3>
                <button onClick={() => setShowCreateModal(false)}>
                  <X className="w-5 h-5 text-warm-500" />
                </button>
              </div>
              <form onSubmit={handleCreate} className="p-4 space-y-4">
                <Input
                  label="Display Name"
                  value={newUser.display_name}
                  onChange={(e) => setNewUser((p) => ({ ...p, display_name: e.target.value }))}
                  placeholder="Professional Communicator"
                  required
                />
                <Textarea
                  label="Description (optional)"
                  value={newUser.description}
                  onChange={(e) => setNewUser((p) => ({ ...p, description: e.target.value }))}
                  placeholder="High formality, direct communication style..."
                  rows={3}
                />
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="ghost" onClick={() => setShowCreateModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" isLoading={createUser.isPending}>
                    Create
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Distillation Modal */}
      <AnimatePresence>
        {showDistillModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDistillModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-warm-200 sticky top-0 bg-white">
                <h3 className="font-semibold text-midnight-800">Distill: {showDistillModal}</h3>
                <button onClick={() => setShowDistillModal(null)}>
                  <X className="w-5 h-5 text-warm-500" />
                </button>
              </div>
              <div className="p-4">
                <DistillationPanel canonicalId={showDistillModal} onSuccess={() => {}} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
